import React, { useState, useEffect, useRef } from 'react'
import { CSSTransition } from 'react-transition-group'
import './listicle.scss'
import { getPersonalityData } from 'services/break.js'
import analytics from 'global/AnalyticsData'
import { scrollToTopOfEditorialStep } from 'services/break'
import { sleep } from 'util/sleep'
import IntersectionVisible from 'components/IntersectionVisible'
import eventEmitter from 'steps/shared/eventEmitter'
import ListicleVoteButtons from './ListicleVoteButtons'
import { getListicleVotes } from 'services/listicle'
import { PuckListicleVoteContext } from './PuckListicleVoteContext'
import { ANIMATION_DURATION, DISPLAY_FORMATS } from './constants'
import { ListiclePanel } from './ListiclePanel'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver'

const ListicleStep = ({
  breakId,
  step,
  onAwardPoints,
  onEngage,
  userType,
  service,
  skipButton,
  nextStepButton,
  // puck-authored listicles render via PuckStep (no StepService). When true we
  // fetch vote tallies with the standalone service and skip legacy content
  // filtering (puck items carry `puckRows`, not `listicleContents`).
  isPuck = false,
  // optional inline style for the `.step-content` card (puck Card Background /
  // Border fields). undefined for non-puck so existing styling is untouched.
  puckCardStyle = undefined,
  // overlay layer for card background image opacity (puck). rendered as an
  // absolute child of `.step-content`. undefined for non-puck.
  puckCardOverlayStyle = undefined,
  // optional inline style for the list-format headline (puck headline
  // typography fields). undefined for non-puck.
  puckHeadlineStyle = undefined,
}) => {

  const [state, setState] = useState({
    position: 0,
    showPanel: true,
    scrollDirection: 'forward',
  })

  const [listItems, setListItems] = useState(step.listicle.items)
  const [voteData, setVoteData] = useState({})
  const [videoWatched, setVideoWatched] = useState(false)

  const sectionEl = useRef(null)


  const [pqResultId, setPqResultId] = useState(undefined)
  const [inView, entry] = useIntersectionObserver(sectionEl, {
    threshold: 0.1,
  })


  useEffect(() => {
    if (!breakId) return
    getPersonalityData(breakId)
      .then(result => setPqResultId(result))
      .catch(e => console.warn('personalityData fetch failed', e))
  }, [breakId])

  useEffect(() => {
    filterListicleItems()
  }, [pqResultId])

  useEffect(() => {
    const asyncWrapper = async () => {
      try {
        // puck listicles have no StepService; use the standalone fetch keyed on
        // the container's puck id. Both return a fetch Response whose json() is
        // { listicleItemVotes: [...] } so they're consumed identically below.
        const response = isPuck
          ? await getListicleVotes(step.listicle.id)
          : await service.get('vote', { listicleId: step.listicle.id })

        if (response.status === 200) {
          const body = await response.json()

          // turn the api result (array) into a map
          const voteDataObj = (body.listicleItemVotes ?? []).reduce(
            (result, item) => {
              result[item.itemId] = item
              return result
            },
            {},
          )

          setVoteData(voteDataObj)
        }
      } catch (e) {
        // votes are optional — a missing/!=200 endpoint must not break render
        console.warn('listicle vote fetch failed', e)
      }
    }

    ;(service || isPuck) && step && step.listicle.id && asyncWrapper()
  }, [service, step, isPuck])

  const handleNextClick = async () => {
    setState({
      position: state.position,
      showPanel: false,
      scrollDirection: 'forward',
    })
  
    // this gives the CSS animation time to exit
    await sleep(ANIMATION_DURATION + 100)
    scrollToTopOfEditorialStep('auto') // TODO: This obv won't work for sponsored

 
    // points are awarded after the user clicks next to go to the next step
    if (state.position === 0) {
      onAwardPoints()
      onEngage()
      setVideoWatched(true)
    }

    if (state.position === listItems.length - 1) {
      setState({
        position: 0,
        showPanel: true,
        scrollDirection: 'forward',
      })
    } else {
      setState({
        position: state.position + 1,
        showPanel: true,
        scrollDirection: 'forward',
      })
    }

    if (typeof window !== `undefined`) {
      analytics.stepEvent('step_interaction_listicle_arrow_click', step, {})
    }
  }

  const handlePrevClick = async () => {
    // console.log(`prev click ${state.position}`)

    // trigger the fade out animation `showPanel: false`
    setState({
      position: state.position,
      showPanel: false,
      scrollDirection: 'backward',
    })
    await sleep(ANIMATION_DURATION + 100)

    // trigger the fade in animation on the next item `showPanel: true`
    if (state.position === 0) {
      setState({
        position: listItems.length - 1,
        showPanel: true,
        scrollDirection: 'backward',
      })
    } else {
      setState({
        position: state.position - 1,
        showPanel: true,
        scrollDirection: 'backward',
      })
    }

    analytics.stepEvent('step_interaction_listicle_arrow_click', step, {})
  }

  const handleIsOnScreen = isIntersecting => {
    if (isIntersecting && breakId) {
      getPersonalityData(breakId)
        .then(result => setPqResultId(result))
        .catch(e => console.warn('personalityData fetch failed', e))
    }
  }

  const handleIsOnScreenVertical = isIntersecting => {
    if (isIntersecting) {
      onAwardPoints()
      onEngage()
      analytics.stepEvent('step_interaction_listicle_scroll_complete', step, {})
      setVideoWatched(true)
      console.log(videoWatched)
    }
  }

  const handleOnVote = (itemId, selection) => {
    // voteData may not have an entry yet (e.g. puck listicle with no prior
    // tallies fetched) — start from zeroes so we never dereference undefined.
    let tmp = voteData[itemId] || { upvotes: 0, downvotes: 0 }
    tmp.selection = selection
    if (selection === 'UP') {
      tmp.upvotes = tmp.upvotes + 1
    }
    if (selection === 'DOWN') {
      tmp.downvotes = tmp.downvotes + 1
    }

    voteData[itemId] = tmp

    setVoteData(voteData)
  }

  const filterListicleItems = async () => {
    let newListicleItems = step.listicle.items

    try {
      // on sponsored breaks listicle items can be filtered down based on answers to a previous steps
     
      //filter listicle items based on pqresultid
      newListicleItems = step.listicle.items.filter(item => {
        const hide =
          item.dynamics &&
          item.dynamics.some(dynamic => dynamic.value === pqResultId)
        if (hide) {
          return false
        }
        return true
      })
    } catch (e) {
      console.log(e)
    }

    // the editorial team hard-coded footers in content asking people to sign up.
    // they did this is hundreds of breaks.
    // we remove those programmatically at render time.
    // there are 2 cases:
    // 1) signup copy (bodyCopy) is the only content item in listicleContents[]
    //      when we remove that content we also remove the parent item (which is now empty of contents)
    //      that's what's happening below
    // 2) signup copy is one of several content items in listicleContents[]
    //      we do nothing and let <ListicleContent> just not display it later because
    //      there are other listicleContents items that should get displayed
    newListicleItems = newListicleItems.filter(item => {
      // puck items render via `puckRows` and have no legacy listicleContents to
      // scan for hard-coded signup copy — keep them as-is.
      if (!item.listicleContents) return true
      // console.log(item)
      return item.listicleContents.some(content => {
        // this is awful and I'm sorry
        // !content.bodyCopy - anything that isn't a bodyCopy element is good
        // bodyCopy and not "not a memeber" is also good
        return (
          !content.bodyCopy ||
          (content.bodyCopy &&
            content.bodyCopy.indexOf('Not a member of Dailybreak yet') === -1)
        )
      })
    })

    if (newListicleItems.length !== step.listicle.items.length) {
      setListItems(newListicleItems)
    }
  }

  // console.log(`listItems`, listItems)
  // console.log(`state`, state)
  const item = listItems[state.position]

  // Build the vote UI for an item. Puck items carry a pre-rendered ListicleVoting
  // block (puckVotingContent) which reads its state from PuckListicleVoteContext;
  // legacy items use the classic ListicleVoteButtons.
  const buildVoteComponent = (voteItem, position, total) => {
    if (!voteItem || !voteItem.hasVoting) return false
    const vd = voteData[voteItem.id]
    if (voteItem.puckVotingContent) {
      return (
        <PuckListicleVoteContext.Provider
          value={{
            listicleId: step.listicle.id,
            itemId: voteItem.id,
            step,
            itemTotal: total,
            itemPosition: position,
            selection: vd ? vd.selection : undefined,
            upvotes: vd ? vd.upvotes : undefined,
            downvotes: vd ? vd.downvotes : undefined,
            onVote: handleOnVote,
          }}
        >
          {voteItem.puckVotingContent}
        </PuckListicleVoteContext.Provider>
      )
    }
    return (
      <ListicleVoteButtons
        step={step}
        item={voteItem}
        item_position={position}
        item_total={total}
        listicleId={step.listicle.id}
        itemId={voteItem.id}
        upVotetxt={voteItem.upvoteBtnText}
        downVotetxt={voteItem.downvoteBtnText}
        upVoteIcon={voteItem.upvoteBtnIcon}
        downVoteIcon={voteItem.downvoteBtnIcon}
        selection={vd ? vd.selection : undefined}
        upvotes={vd ? vd.upvotes : undefined}
        downvotes={vd ? vd.downvotes : undefined}
        onVote={handleOnVote}
      />
    )
  }

  const voteButtonComponent = buildVoteComponent(
    item,
    state.position,
    listItems.length,
  )

  const voteButtonComponentVertical = index =>
    buildVoteComponent(
      step.listicle.items[index],
      index,
      step.listicle.items.length,
    )

  if (!step.listicle.displayFormat) {
    step.listicle.displayFormat = DISPLAY_FORMATS.SLIDESHOW
  }

  return (
      <section
      ref={sectionEl}
        className={`listicle-step format-${step.listicle.displayFormat}`}
      >
        <IntersectionVisible
          active={true}
          options={{
            threshold: 0.4,
          }}
          onIntersect={handleIsOnScreen}
        >
          <div className="visible-handler-top" style={{ height: '1px' }}>
            &nbsp;
          </div>
        </IntersectionVisible>
        {step.listicle.displayFormat === DISPLAY_FORMATS.LIST ? (
          <div className='step-content with-shadow' style={puckCardStyle}>
            {puckCardOverlayStyle && <div style={puckCardOverlayStyle} />}

            <div className="step-intro">
            { step.listicle.headline && (
              <h2
                className="step-content__title"
                style={puckHeadlineStyle}
                dangerouslySetInnerHTML={{ __html: step.listicle.headline }}
              />
            )}
            </div>
            {listItems.map((_, index) => (
              <ListiclePanel
                key={index}
                step={step}
                item={listItems[index]}
                i={index}
                currentPosition={index + 1}
                totalItems={listItems.length}
                backgroundImage={step.backgroundImage}
                voteButtonComponent={voteButtonComponentVertical(index)}
                skipButton={skipButton}
              />
            ))}
              <IntersectionVisible
                active={true}
                options={{
                  threshold: 0.4,
                }}
                onIntersect={handleIsOnScreenVertical}
              >
                <div
                  className="visible-handler-bottom"
                  style={{ height: '10px' }}
                >
                  &nbsp;
                </div>
              </IntersectionVisible>
              {nextStepButton}
              </div>
        ) : (
          <div className={`slideshow db-react-slideshow ${state.scrollDirection} `}>

          <CSSTransition
            in={state.showPanel}
            timeout={ANIMATION_DURATION}
            classNames="db-react-slide"
            unmountOnExit={false}
          >
            <div className={`step-content with-shadow `} style={puckCardStyle}>
                {puckCardOverlayStyle && <div style={puckCardOverlayStyle} />}
                <ListiclePanel
                  step={step}
                  item={listItems[state.position]}
                  i={state.position}
                  currentPosition={state.position + 1}
                  totalItems={listItems.length}
                  backgroundImage={step.backgroundImage}
                  handlePrevClick={handlePrevClick}
                  handleNextClick={handleNextClick}
                  voteButtonComponent={voteButtonComponent}
                  skipButton={skipButton}
                />

            </div>
          </CSSTransition>
          <div className="next-arrow-slideshow">{nextStepButton}</div>
          </div>

        )}
      </section>
  )
}

export default ListicleStep
